import { useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  TICKETLIST_FETCH_BEGIN,
  TICKETLIST_FETCH_SUCCESS,
  TICKETLIST_FETCH_FAILURE,
  USER_TICKET_LIST_FETCH_BEGIN,
  USER_TICKET_LIST_FETCH_SUCCESS,
  USER_TICKET_LIST_FETCH_FAILURE,
  UPDATE_TICKET_LIST_FILTER,
  UPDATE_TICKET_LIST_NEW_MESSAGE,
} from "./constants";
import _ from "lodash";
import axios from "axios";
import { apiUrl } from "features/configure";

export const TICKET_FILTER_INITIAL_STATE = {
  ticketListFilter: {
    ticketStatus: "new",
    page: 1,
    orderData: { sort: "lastUserResponse", order: -1 },
    assigneeOrCollaborator: [],
    channel: [],
    issue: [],
    addressStatus: "",
    searchTags: [],
    searchText: "",
  },
};

const formatTicketStatus = (status) => {
  if (status === "ai") {
    return "AI";
  }
  return _.upperFirst(status);
};

export function fetchTicketList({ entityName, filter, status }) {
  return (dispatch, getState) => {
    const ticketStatus = status || _.get(filter, "ticketStatus", "");
    if (!ticketStatus) return; //prevent init fetch
    dispatch({
      type: TICKETLIST_FETCH_BEGIN,
    });
    let data = {
      sources: _.get(filter, "channel", []),
      status: formatTicketStatus(ticketStatus),
      entityName: entityName,
      page: _.get(filter, "page", 1),
      hasAddress: _.get(filter, "addressStatus", "All"),
      issue: _.get(filter, "issue", ""),
      assigneeOrCollaborator: _.get(filter, "assigneeOrCollaborator", ""),
      searchTags: _.get(filter, "searchTags", ""),
      searchText: _.get(filter, "searchText", ""),
      sort: _.get(filter, "orderData.sort"),
      order: _.get(filter, "orderData.order"),
    };
    const promise = new Promise(async (resolve, reject) => {
      try {
        let ticketList = await axios.get(`${apiUrl}/api/ticket/ticketList`, {
          params: data,
        });

        dispatch({
          type: TICKETLIST_FETCH_SUCCESS,
          data: ticketList.data,

          ticketCounts: ticketList.headers["x-total-count"],
        });

        resolve();
      } catch (err) {
        dispatch({
          type: TICKETLIST_FETCH_FAILURE,
        });
        // console.log(err)
      }
    });

    return promise;
  };
}
export function updateTicketListFilter(data) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_TICKET_LIST_FILTER,
      data,
    });
  };
}

export function updateTicketListNewMessage(ticketId) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_TICKET_LIST_NEW_MESSAGE,
      ticketId,
    });
  };
}

export function fetchUserTicketList({
  entityName,
  userIds,
  uid,
  filter,
  limit = 20,
}) {
  return (dispatch, getState) => {
    dispatch({
      type: USER_TICKET_LIST_FETCH_BEGIN,
    });

    const promise = new Promise(async (resolve, reject) => {
      try {
        const params = {
          ...(uid ? { portalUserId: uid[0] } : { userIds }),
          entityName,
          page: _.get(filter, "page", 1),
          sort: _.get(filter, "orderData.sort"),
          order: _.get(filter, "orderData.order"),
          limit: limit,
        };

        let userTickets = await axios.get(`${apiUrl}/api/ticket/user`, {
          params,
        });

        dispatch({
          type: USER_TICKET_LIST_FETCH_SUCCESS,
          data: userTickets.data,
          userIds,
          userTicketCounts: parseInt(userTickets.headers["x-total-count"]),
        });
        resolve();
      } catch (err) {
        dispatch({
          type: USER_TICKET_LIST_FETCH_FAILURE,
        });
        reject();
        console.log(err);
      }
    });

    return promise;
  };
}

export function useFetchTicketList() {
  const dispatch = useDispatch();

  const {
    ticketList,
    fetchTicketListPending,
    fetchTicketListDone,
    userTicketList,
    ticketCounts,
    fetchUserTicketListPending,
    ticketListFilter,
    userTicketCounts,
    userTicketAvgScoring,
  } = useSelector(
    (state) => ({
      ticketCounts: state.metadesk.ticketCounts,
      ticketList: state.metadesk.ticketList,
      statsCounts: state.metadesk.statsCounts,
      fetchTicketListPending: state.metadesk.fetchTicketListPending,
      fetchTicketListDone: state.metadesk.fetchTicketListDone,
      userTicketList: state.metadesk.userTicketList,
      fetchUserTicketListPending: state.metadesk.fetchUserTicketListPending,
      ticketListFilter: state.metadesk.ticketListFilter,
      userTicketCounts: state.metadesk.userTicketCounts,
      userTicketAvgScoring: state.metadesk.userTicketAvgScoring,
    }),
    shallowEqual
  );

  const boundAction = useCallback(
    (data) => {
      return dispatch(fetchTicketList(data));
    },
    [dispatch]
  );

  const fetchUserTicketListAction = useCallback(
    (data) => {
      return dispatch(fetchUserTicketList(data));
    },
    [dispatch]
  );

  const updateTicketListFilterAction = useCallback(
    (data) => {
      return dispatch(updateTicketListFilter(data));
    },
    [dispatch]
  );

  const updateTicketListNewMessageAction = useCallback(
    (data) => {
      return dispatch(updateTicketListNewMessage(data));
    },
    [dispatch]
  );

  return {
    ticketList,
    fetchTicketList: boundAction,
    updateTicketListFilter: updateTicketListFilterAction,
    fetchTicketListDone,
    fetchTicketListPending,
    userTicketList,
    ticketListFilter,
    ticketCounts,
    userTicketCounts,
    userTicketAvgScoring,
    fetchUserTicketList: fetchUserTicketListAction,
    updateTicketListNewMessage: updateTicketListNewMessageAction,
    fetchUserTicketListPending,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case TICKETLIST_FETCH_BEGIN:
      return {
        ...state,
        fetchTicketListPending: true,
      };
    case TICKETLIST_FETCH_SUCCESS:
      return {
        ...state,
        ticketList: action.data,
        fetchTicketListDone: true,
        ticketCounts: action.ticketCounts,
        fetchTicketListPending: false,
      };
    case UPDATE_TICKET_LIST_NEW_MESSAGE:
      return {
        ...state,
        ticketList: _.map(state.ticketList, (ticket) =>
          ticket._id === action.ticketId
            ? { ...ticket, hasNewMessage: false }
            : ticket
        ),
      };
    case TICKETLIST_FETCH_FAILURE:
      return {
        ...state,
        fetchTicketListPending: false,
      };

    case USER_TICKET_LIST_FETCH_BEGIN:
      return {
        ...state,
        fetchUserTicketListPending: true,
      };

    case USER_TICKET_LIST_FETCH_SUCCESS:
      return {
        ...state,
        userTicketList: action.data.tickets,
        userTicketCounts: action.userTicketCounts,
        userTicketAvgScoring: action.data.avgScoring,
        fetchUserTicketListPending: false,
      };

    case USER_TICKET_LIST_FETCH_FAILURE:
      return {
        ...state,
        fetchUserTicketListPending: false,
      };

    case UPDATE_TICKET_LIST_FILTER:
      return {
        ...state,
        ticketListFilter: { ...state.ticketListFilter, ...action.data },
      };

    default:
      return state;
  }
}
